/**
 * Shared chrome (header/nav/footer) for every static marketing page
 * (GitHub issue — "Create React components for static marketing pages
 * using vite-react-ssg").
 *
 * Requirement #4 ("fully accessible (WCAG 2.1 AA), responsive, and
 * styled with Tailwind CSS and shadcn/ui primitives"):
 * - A "skip to main content" link is the first focusable element on the
 *   page (visually hidden until focused) so keyboard/screen-reader users
 *   can bypass repeated header/nav markup — WCAG 2.4.1 (Bypass Blocks).
 * - `<header>`/`<nav aria-label>`/`<main id="main-content">`/`<footer>`
 *   are real landmark elements, and the primary nav is labelled so
 *   assistive tech can distinguish it from the footer nav.
 * - The language toggle is a real, keyboard-operable `<button>` with an
 *   explicit `aria-label` (not an icon-only control with no accessible
 *   name).
 *
 * Requirement #7 ("no client-side API calls or auth dependencies exist in
 * these static pages"): this layout only reads/writes `i18next`'s
 * in-memory language state (`i18n.changeLanguage`) — no network request,
 * no Supabase/auth import. It stays 100% SSG-safe.
 *
 * Importing `../../lib/i18n` here (rather than separately in every page)
 * guarantees the i18next instance is initialized before any page renders,
 * since every marketing page renders through this layout.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { SUPPORTED_LOCALES, type SupportedLocale } from '@/lib/i18n';

export interface MarketingLayoutProps {
  children: ReactNode;
  className?: string;
}

export default function MarketingLayout({
  children,
  className,
}: MarketingLayoutProps): JSX.Element {
  const { t, i18n } = useTranslation();

  const toggleLocale = (): void => {
    const current = (i18n.language?.slice(0, 2) as SupportedLocale) || 'en';
    const next: SupportedLocale = current === 'en' ? 'es' : 'en';
    void i18n.changeLanguage(next);
  };

  const otherLocale: SupportedLocale =
    (i18n.language?.slice(0, 2) as SupportedLocale) === 'en' ? 'es' : 'en';

  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
      >
        {t('common.skipToContent')}
      </a>

      <header className="border-b">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <Link to="/" className="text-2xl font-bold">
            {t('common.brand')}
          </Link>
          <nav aria-label={t('common.nav.ariaLabel')} className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleLocale}
              aria-label={`Switch language to ${otherLocale === 'es' ? 'Español' : 'English'}`}
              className={cn(
                'rounded-md px-2 py-1 text-sm text-muted-foreground hover:text-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
            >
              {SUPPORTED_LOCALES.includes(otherLocale) ? otherLocale.toUpperCase() : 'EN'}
            </button>
            <Button variant="ghost" asChild>
              <Link to="/login">{t('common.nav.signIn')}</Link>
            </Button>
            <Button asChild>
              <Link to="/getstarted">{t('common.nav.getStarted')}</Link>
            </Button>
          </nav>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className={cn('flex-1', className)}>
        {children}
      </main>

      <footer className="border-t py-8">
        <div className="container mx-auto flex flex-col items-center gap-4 px-4 text-center text-muted-foreground">
          <nav aria-label={t('common.footer.ariaLabel')} className="flex gap-6">
            <Link to="/privacy" className="hover:text-foreground">
              {t('common.footer.privacy')}
            </Link>
            <Link to="/terms" className="hover:text-foreground">
              {t('common.footer.terms')}
            </Link>
            <Link to="/contact" className="hover:text-foreground">
              {t('common.footer.contact')}
            </Link>
          </nav>
          <p>{t('common.footer.copyright')}</p>
        </div>
      </footer>
    </div>
  );
}
